const TARGET_PARAMS = ["camera", "lighting"] as const;
const TARGET_LABELS: Record<(typeof TARGET_PARAMS)[number], string> = {
  camera: "Camera",
  lighting: "Lighting",
};
const GENRE_LABEL = "Genre";
const FRAGMENT_SEPARATOR = "; ";
const PROMPT_SEPARATOR = "\n\n";
const AUDIO_DIRECTION_PREFIX = `${PROMPT_SEPARATOR}Creative direction: `;
const AUDIO_DIRECTION_SUFFIX = " genre.";
const GENRE_MAX_LENGTH = 40;
const GENRE_MAX_WORDS = 3;
const GENRE_PATTERN = /^[^\W_][\w \-/&,.'’]*$/u;

export const REFERENCE_FIELDS: Record<string, "image" | "video" | "audio"> = {
  image: "image",
  end_image: "image",
  source_video: "video",
  reference_images: "image",
  reference_videos: "video",
  reference_audios: "audio",
};

const DISCARDED_PARAMS = [
  "resolved_mentions",
  "creative_direction_resolved",
  "previous_interaction_id",
  "prompt_manual",
  "camera_manual",
  "lighting_manual",
];

export interface RecreateReference {
  asset_id: string;
  type: string;
  mime: string;
  storage_key?: string;
  thumbnail_storage_key?: string | null;
}

export type RecreateModality = "image" | "video" | "audio";

export interface RecreateDraft {
  params: Record<string, unknown>;
  /** Recovered only when a client-composed audio genre was found in the prompt. */
  genre: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function appendedTargets(snapshot: Record<string, unknown>): Record<string, string> {
  const targets = asRecord(snapshot.targets);
  if (targets) {
    return Object.fromEntries(
      TARGET_PARAMS
        .map((target) => [target, asText(targets[target])] as const)
        .filter(([, value]) => Boolean(value))
    );
  }
  const slots = Array.isArray(snapshot.slots) ? snapshot.slots : [];
  const collected: Record<string, string> = {};
  for (const target of TARGET_PARAMS) {
    const fragments = slots
      .map((raw) => asRecord(raw))
      .filter((row): row is Record<string, unknown> => row?.target_param === target)
      .map((row) => asText(row.prompt_fragment))
      .filter(Boolean);
    if (fragments.length) collected[target] = fragments.join(FRAGMENT_SEPARATOR);
  }
  return collected;
}

function orderedSubsets(items: string[]): string[][] {
  return items.reduce<string[][]>(
    (accumulated, item) => accumulated.concat(accumulated.map((subset) => [...subset, item])),
    [[]]
  );
}

function withoutResolvedFragments(prompt: string, snapshot: Record<string, unknown>): string {
  const targets = appendedTargets(snapshot);
  const extras = [
    asText(snapshot.genre) ? `${GENRE_LABEL}: ${asText(snapshot.genre)}` : "",
    ...TARGET_PARAMS.map((target) =>
      targets[target] ? `${TARGET_LABELS[target]}: ${targets[target]}` : ""
    ),
  ].filter(Boolean);

  const blocks = orderedSubsets(extras)
    .filter((subset) => subset.length > 0)
    .map((subset) => subset.join("\n"))
    .sort((left, right) => right.length - left.length);

  for (const block of blocks) {
    if (prompt === block) return "";
    const tail = `${PROMPT_SEPARATOR}${block}`;
    if (prompt.endsWith(tail)) return prompt.slice(0, prompt.length - tail.length);
  }
  return prompt;
}

function isGenreLabel(value: string): boolean {
  const words = value.split(/\s+/).filter(Boolean);
  return (
    value.length > 0
    && value.length <= GENRE_MAX_LENGTH
    && words.length <= GENRE_MAX_WORDS
    && GENRE_PATTERN.test(value)
  );
}

function splitAudioGenre(prompt: string): { prompt: string; genre: string | null } {
  if (!prompt.endsWith(AUDIO_DIRECTION_SUFFIX)) return { prompt, genre: null };
  const start = prompt.lastIndexOf(AUDIO_DIRECTION_PREFIX);
  if (start < 0) return { prompt, genre: null };
  const genre = prompt.slice(start + AUDIO_DIRECTION_PREFIX.length, prompt.length - AUDIO_DIRECTION_SUFFIX.length);
  if (!isGenreLabel(genre)) return { prompt, genre: null };
  return { prompt: prompt.slice(0, start), genre };
}

export function normalizeReference(value: unknown, fallbackType: string): RecreateReference | null {
  const row = asRecord(value);
  const assetId = asText(row?.asset_id);
  if (!row || !assetId) return null;
  const reference: RecreateReference = {
    asset_id: assetId,
    type: asText(row.type) || fallbackType,
    mime: asText(row.mime),
  };
  if (asText(row.storage_key)) reference.storage_key = asText(row.storage_key);
  if (asText(row.thumbnail_storage_key)) {
    reference.thumbnail_storage_key = asText(row.thumbnail_storage_key);
  }
  return reference;
}

function restoreReferences(params: Record<string, unknown>): void {
  for (const [field, fallbackType] of Object.entries(REFERENCE_FIELDS)) {
    if (!(field in params)) continue;
    const raw = params[field];
    if (Array.isArray(raw)) {
      const references = raw
        .map((entry) => normalizeReference(entry, fallbackType))
        .filter((entry): entry is RecreateReference => entry !== null);
      if (references.length) params[field] = references;
      else delete params[field];
      continue;
    }
    const reference = normalizeReference(raw, fallbackType);
    if (reference) params[field] = reference;
    else delete params[field];
  }
}

/**
 * Rebuild composer parameters from a job's frozen params so that re-quoting the
 * same selections reproduces the same generation instead of compounding it.
 */
export function recreateDraft(
  frozen: Record<string, unknown>,
  modality: RecreateModality
): RecreateDraft {
  const params: Record<string, unknown> = { ...frozen };
  if (Array.isArray(frozen.resolved_mentions) && frozen.resolved_mentions.length) {
    params.reference_bindings = frozen.resolved_mentions.map((raw) => {
      const entry = asRecord(raw) ?? {};
      return { token: entry.token, kind: entry.kind, id: entry.id, ...(entry.updated_at ? { updated_at: entry.updated_at } : {}) };
    });
  }
  const snapshot = asRecord(frozen.creative_direction_resolved);
  let genre: string | null = null;

  if (typeof frozen.prompt_manual === "string") {
    params.prompt = frozen.prompt_manual;
  } else if (typeof frozen.prompt === "string") {
    if (snapshot) {
      params.prompt = withoutResolvedFragments(frozen.prompt, snapshot);
    } else if (modality === "audio") {
      const split = splitAudioGenre(frozen.prompt);
      params.prompt = split.prompt;
      genre = split.genre;
    }
  }

  for (const target of TARGET_PARAMS) {
    const manual = frozen[`${target}_manual`];
    if (typeof manual === "string") params[target] = manual;
  }

  for (const key of DISCARDED_PARAMS) delete params[key];
  for (const key of Object.keys(params)) {
    if (key.startsWith("_")) delete params[key];
  }
  restoreReferences(params);

  return { params, genre };
}
